import { http } from '@/api/client/http';
import {
  TenantListResponseSchema,
  TenantSuccessResponseSchema,
  isSuspendEdgeLegal,
} from '@/api/schemas/tenants';
import type {
  CreateTenantRequest,
  Tenant,
  ChangeTenantStatusRequest,
} from '@/api/schemas/tenants';

/**
 * Module-level service: every API call this module makes passes through here.
 *
 * There is ONE source now. The stub that used to answer the list and detail pages
 * is gone — it was there because the API exposed no tenant list endpoint, and that
 * endpoint now exists (`GET /api/v1/tenants`, contract
 * `contracts/foundation.register.md` §2.3). A page that renders fixture data is a page
 * that lies about the deployment, and the demo's whole point is that what the
 * console shows is what the platform holds.
 */
export const liveApi = {
  /**
   * GET /tenants — the platform's tenant registry.
   *
   * PLATFORM-scoped: the caller needs the `tenant.read` grant. A caller without it
   * receives `403`, which the list page renders as a refusal rather than as an
   * empty list — an empty list would be indistinguishable from a deployment with no
   * merchants.
   */
  async list(): Promise<{ tenants: Tenant[]; total: number }> {
    const response = await http.get('/tenants', TenantListResponseSchema);
    return { tenants: response.data.tenants, total: response.data.tenants.length };
  },

  /** POST /tenants — create an active tenant, optionally with an initial owner and profile. */
  create: (input: CreateTenantRequest) =>
    http.post('/tenants', TenantSuccessResponseSchema, {
      body: input,
      idempotencyKey: crypto.randomUUID(),
    }),

  /** GET /tenants/{id} — platform tenant detail. */
  async get(id: string): Promise<Tenant | null> {
    try {
      const response = await http.get(`/tenants/${id}`, TenantSuccessResponseSchema);
      return response.data;
    } catch (error) {
      // 404 means not found or not authorized
      return null;
    }
  },

  /** PATCH /tenants/{id}/status — currently only supports active -> suspended. */
  changeStatus: (id: string, input: ChangeTenantStatusRequest) =>
    http.patch(`/tenants/${id}/status`, TenantSuccessResponseSchema, {
      body: input,
      ifMatch: 1,
      idempotencyKey: crypto.randomUUID(),
    }),
};

/**
 * Looks up one tenant in the registry.
 *
 * A client-side filter over the registry rather than a second endpoint: the API
 * exposes no single-tenant read for the platform scope, and a second endpoint for
 * one row would be a second vocabulary.
 */
export async function getTenant(id: string): Promise<Tenant | null> {
  return liveApi.get(id);
}

/** The two functions the rest of the module imports. */
export const TenantApi = liveApi;

export { isSuspendEdgeLegal };
export type { CreateTenantRequest, Tenant, ChangeTenantStatusRequest };
